import { LegacyRef, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Select,
  SelectAction,
  SelectContent,
  SelectItem,
  SelectValue,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsItem,
  TabsList,
  toast,
  Upload,
  UploadBody,
  UploadText,
} from 'keep-react'

import { productService } from 'api/services/product'
import { Wrapper } from 'components'
import { ProductCategory, ProductType } from 'api/types/product'
import Quill from 'quill'
import { Editor } from 'components/QuillEditor'
import { ArrowLeft, FloppyDisk, Trash } from 'phosphor-react'
import { RouteNamesAuth } from 'routes/enum'

export interface UploadFile {
  label: string
  file: File
}

// form validation schema
const validationSchema = Yup.object().shape({
  categoryId: Yup.number(),
  status: Yup.number().oneOf([0, 1]),
  hasSimulation: Yup.boolean(),
  productFiles: Yup.mixed().nullable(),
})

export const BackOfficeProduct = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  //loaded product data
  const [data, setData] = useState<ProductType | null>(null)
  const [categories, setCategories] = useState<ProductCategory[]>([])

  // Quill States
  const [content, setContent] = useState<string>('')
  const [helperText, setHelperText] = useState<string>('')
  const [conditions, setConditions] = useState<string>('')

  // Modal Control
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  // Save Button Active?
  const [hasChanges, setHasChanges] = useState<boolean>(false)

  // Files to upload
  const [file, setFile] = useState<File | null>(null)

  // Delete File Modal
  const [showDeleteFileModal, setShowDeleteFileModal] = useState<boolean>(false)

  // File to delete
  const [fileToDelete, setFileToDelete] = useState<string | null>(null)

  // Editor Ref
  const quillContentRef = useRef(null)

  //get product details
  const getProduct = async () => {
    setIsLoading(true)
    try {
      const response = (await productService.getPortalProduct({
        product: id as string,
      })) as ProductType
      setData(response as ProductType)
      formik.setFieldValue('product', response.product)
      formik.setFieldValue('categoryId', response.categoryId)
      formik.setFieldValue('description', response.description)
      setContent(response.content)
      formik.setFieldValue('content', response.content)
      setHelperText(response.simulatorHelperText)
      formik.setFieldValue('simulatorHelperText', response.simulatorHelperText)
      setConditions(response.simulatorConditions)
      formik.setFieldValue('simulatorConditions', response.simulatorConditions)
      formik.setFieldValue('status', response.status)
      formik.setFieldValue('hasSimulation', response.hasSimulation)
      formik.setFieldValue('label', response.label)

      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar os produtos.')
    }
  }

  //get categories for the dropdown
  const getCategories = async () => {
    try {
      const response = await productService.getCategories()
      setCategories(response as ProductCategory[])
    } catch (error) {
      toast.error('Não foi possível carregar as categorias.')
    }
  }

  // delete selected file
  const deleteFile = async () => {
    if (!data || !fileToDelete) return
    try {
      setIsApiBeingCalled(true)
      await productService.removePortalProductFile({
        product: data.product,
        fileId: fileToDelete,
      })
      setShowDeleteFileModal(false)
      toast.success('Ficheiro apagado com sucesso.')
      // refresh product data
      getProduct()
    } catch (error) {
      toast.error('Não foi possível apagar o ficheiro.')
    } finally {
      setIsApiBeingCalled(false)
    }
  }

  // using formik to handle form state
  const formik = useFormik({
    initialValues: {
      product: '',
      categoryId: 0,
      description: '',
      content: '',
      simulatorHelperText: '',
      simulatorConditions: '',
      status: 0,
      hasSimulation: false,
      productFiles: null,
      label: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      if (!data) return
      try {
        setIsApiBeingCalled(true)
        await productService.savePortalProduct({
          product: data.product,
          categoryId: values.categoryId,
          description: values.description,
          content: content,
          simulatorHelperText: helperText,
          simulatorConditions: conditions,
          status: values.status as 0 | 1,
          hasSimulation: values.hasSimulation,
          label: values.label,
        })
        // setData({ ...data, ...response } )
        toast.success('Produto atualizado com sucesso.')
        setHasChanges(false)
      } catch (error) {
        toast.error('Erro ao atualizar produto.')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  // formik for file upload
  const uploadFormik = useFormik({
    initialValues: {
      label: '',
      file: null,
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async () => {
      if (!data || !file) return
      try {
        setIsApiBeingCalled(true)
        await productService.addPortalProductFiles({
          product: data.product,
          files: {
            label: uploadFormik.values.label,
            file: file,
          },
        })
        setFile(null)
        uploadFormik.setFieldValue('label', '')
        // refresh product data
        getProduct()
        toast.success('Ficheiro(s) carregado(s) com sucesso.')
      } catch (error) {
        toast.error('Erro ao carregar ficheiro(s).')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  // delete selected product
  const deleteProduct = async () => {
    if (!data) return
    try {
      await productService.deletePortalProduct({
        product: data.product,
      })
      setShowDeleteModal(false)
      toast.success('Produto apagado com sucesso.')
      navigate(RouteNamesAuth.BACKOFFICE_PRODUCTS)
    } catch (error) {
      toast.error('Não foi possível apagar o produto.')
    }
  }

  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    setFile(acceptedFiles && acceptedFiles.length > 0 ? acceptedFiles[0] : null)
  }, [])

  // initial data fetch
  useEffect(() => {
    getProduct()
    getCategories()
  }, [])

  // track changes to enable save button
  useEffect(() => {
    setHasChanges(true)
  }, [content, helperText, conditions, formik.values])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading || !data ? (
          <Card className="w-full max-w-full p-4 md:p-6">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Button
              variant="outline"
              color="secondary"
              className="mb-6"
              onClick={() => navigate(RouteNamesAuth.BACKOFFICE_PRODUCTS)}
            >
              <ArrowLeft size={16} /> Produtos
            </Button>
            <Card className="w-full max-w-full p-4 md:p-6">
              <h1 className="pb-10 text-xxxl font-medium text-metal-900 flex items-center justify-between">
                <span>
                  {data.product} - {data.description}
                </span>
                <>
                  <ButtonGroup>
                    <Button
                      type="submit"
                      position="start"
                      color="secondary"
                      variant="default"
                      disabled={!hasChanges || isApiBeingCalled}
                      className="gap-2"
                      onClick={() => {
                        formik.handleSubmit()
                      }}
                    >
                      <FloppyDisk size={16} />
                      Gravar alterações
                    </Button>

                    <Button
                      position="end"
                      variant="default"
                      color="error"
                      onClick={() => {
                        setShowDeleteModal(true)
                      }}
                      disabled={isApiBeingCalled}
                      className="gap-2"
                    >
                      <Trash size={16} />
                      Apagar Produto
                    </Button>
                  </ButtonGroup>
                </>
              </h1>

              <Tabs defaultValue="general">
                <TabsList>
                  <TabsItem value="general">Geral</TabsItem>
                  <TabsItem value="simulator">Simulador</TabsItem>
                  <TabsItem value="documents">Documentos</TabsItem>
                </TabsList>

                <TabsContent value="general" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Categoria
                      </h3>
                      <Select
                        value={String(formik.values.categoryId ?? '')}
                        onValueChange={value => {
                          formik.setFieldValue('categoryId', Number(value))
                        }}
                      >
                        <SelectAction>
                          <SelectValue>
                            {categories.find(
                              c =>
                                Number(c.id) ===
                                Number(formik.values.categoryId),
                            )?.name || 'Selecione uma categoria'}
                          </SelectValue>
                        </SelectAction>
                        <SelectContent>
                          {categories.map(category => (
                            <SelectItem
                              key={category.id}
                              value={String(category.id)}
                            >
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Status
                      </h3>
                      <span className="flex items-center gap-2">
                        {data.status === 1 ? (
                          <Badge
                            className="pointer-events-none"
                            color="success"
                            variant="base"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            className="pointer-events-none"
                            color="error"
                            variant="base"
                          >
                            Inativo
                          </Badge>
                        )}
                        <Switch
                          checked={formik.values.status === 1}
                          onCheckedChange={() => {
                            setHasChanges(true)
                            formik.setFieldValue(
                              'status',
                              formik.values.status === 1 ? 0 : 1,
                            )
                          }}
                          className="data-[state=checked]:bg-metal-500 text-metal-500 [&_span]:data-[state=checked]:before:bg-metal-500"
                        />
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Tem simulador?
                      </h3>
                      <span className="flex items-center gap-2">
                        {data.hasSimulation ? (
                          <Badge
                            className="pointer-events-none"
                            color="success"
                            variant="base"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            className="pointer-events-none"
                            color="error"
                            variant="base"
                          >
                            Inativo
                          </Badge>
                        )}
                        <Switch
                          checked={formik.values.hasSimulation}
                          onCheckedChange={() => {
                            setHasChanges(true)
                            formik.setFieldValue(
                              'hasSimulation',
                              !formik.values.hasSimulation,
                            )
                          }}
                          className="data-[state=checked]:bg-metal-500 text-metal-500 [&_span]:data-[state=checked]:before:bg-metal-500"
                        />
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Conteúdo
                      </h3>
                      <div>
                        <Editor
                          ref={
                            quillContentRef as unknown as
                              | LegacyRef<Quill>
                              | undefined
                          }
                          defaultValue={content}
                          setContent={setContent}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Label do menu
                      </h3>
                      <div>
                        <Input
                          type="text"
                          name="label"
                          value={formik.values.label}
                          onChange={e =>
                            formik.setFieldValue('label', e.currentTarget.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="simulator" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Helper Simulador
                      </h3>
                      <div>
                        <Editor
                          ref={
                            quillContentRef as unknown as
                              | LegacyRef<Quill>
                              | undefined
                          }
                          defaultValue={helperText}
                          setContent={setHelperText}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Condições do Simulador
                      </h3>
                      <div>
                        <Editor
                          ref={
                            quillContentRef as unknown as
                              | LegacyRef<Quill>
                              | undefined
                          }
                          defaultValue={conditions}
                          setContent={setConditions}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="p-4 md:p-6">
                  <>
                    {data.productFiles && data.productFiles.length === 0 && (
                      <p className="text-body-4">
                        Não há documentos disponíveis para este produto.
                      </p>
                    )}

                    {data.productFiles && data.productFiles.length > 0 && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Label</TableHead>
                            <TableHead>Ficheiro</TableHead>
                            <TableHead>Tipo de Conteúdo</TableHead>
                            <TableHead>Tamanho (bytes)</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.productFiles.length > 0 &&
                            data.productFiles.map(file => (
                              <TableRow key={file.id}>
                                <TableCell>{file.label}</TableCell>
                                <TableCell>
                                  <Button variant="link" color="primary">
                                    <Link to={file.uri} target="_blank">
                                      Ver Ficheiro
                                    </Link>
                                  </Button>
                                </TableCell>
                                <TableCell>{file.contentType}</TableCell>
                                <TableCell>{file.sizeBytes}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    color="error"
                                    className="gap-2"
                                    onClick={() => {
                                      setFileToDelete(file.id.toString())
                                      setShowDeleteFileModal(true)
                                    }}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    )}

                    <div className="pt-6">
                      <h3 className="text-xl text-primary-500 pb-4">
                        Adicionar documentos
                      </h3>
                      <form className="flex items-start gap-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          <div>
                            <h3 className="text-body-4 font-bold text-metal-900 pb-2">
                              Label
                            </h3>
                            <Input
                              type="text"
                              name="label"
                              onChange={e =>
                                uploadFormik.setFieldValue(
                                  'label',
                                  e.currentTarget.value,
                                )
                              }
                            />
                          </div>
                          <div>
                            <h3 className="text-body-4 font-bold text-metal-900 pb-2">
                              Ficheiro
                            </h3>
                            <Upload
                              horizontal
                              options={{ onDrop, multiple: false }}
                              className="p-0 border-none"
                            >
                              {file ? (
                                <p className="text-body-4 font-semibold text-metal-600 flex items-center justify-between h-11">
                                  <span>{file.name}</span>
                                  <Button
                                    color="error"
                                    variant="link"
                                    onClick={() => setFile(null)}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </p>
                              ) : (
                                <UploadBody>
                                  <UploadText>
                                    <p className="text-body-4 font-medium text-metal-600">
                                      Escolha ou arraste o seu ficheiro aqui
                                      (PDF, JPG ou WEBP))
                                    </p>
                                  </UploadText>
                                </UploadBody>
                              )}
                            </Upload>
                          </div>
                        </div>
                        <div className="col-span-2 justify-self-end pt-8">
                          <Button
                            disabled={!file || !uploadFormik.values.label}
                            type="button"
                            onClick={() => uploadFormik.handleSubmit()}
                          >
                            Carregar Ficheiro
                          </Button>
                        </div>
                      </form>
                    </div>
                  </>
                </TabsContent>
              </Tabs>
            </Card>
          </>
        )}
      </Wrapper>

      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Produto</ModalTitle>
            <ModalDescription>
              Quer apagar este produto?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados deste produto irão ser perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteProduct()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showDeleteFileModal} onOpenChange={setShowDeleteFileModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Ficheiro</ModalTitle>
            <ModalDescription>
              Quer apagar este ficheiro?
              <br />
              <br />
              <strong className="text-error-500">
                Este ficheiro irá ser apagado.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteFileModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteFile()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
